// THE AFFILIATE PAGES' SHAPE — their addresses, their tabs and the New campaign's
// steps, in the prototype's words, the anchor for everything visible.
//
// Pure, so the pages, their tests and the next PRs that fill each tab in share
// one list: a tab named twice is a tab that will one day be spelled two ways.
//
// ⚠️ EVERY `key` BELOW IS A URL VALUE (`?tab=`, `?step=`, `?t=`, `?view=`), and a
// link somebody pasted into Slack keeps working only while it stays spelled the
// same. Labels are free to change.

import { lensHref } from "@/lib/lens-url";
import { query } from "@/lib/url-query";
import { skillPath } from "@/skills/skills";

export const AFFILIATES_PATH = "/brands/affiliates";
export const AFFILIATE_METRICS_PATH = "/brands/affiliate-metrics";
/** The DM Replies skill page, /skills/dm-replies, where every skill page is. */
export const DM_REPLIES_PATH = skillPath("dm_replies");
export const REACHER_WRITES_PATH = "/settings/integrations/reacher-writes";

export interface AffiliateTab<K extends string = string> {
  key: K;
  label: string;
}

/** A brand's page, `?tab=` — the prototype's five, Campaigns first. */
export const BRAND_TABS = [
  { key: "campaigns", label: "Campaigns" },
  { key: "requests", label: "Sample requests" },
  { key: "messages", label: "Messages" },
  { key: "automations", label: "Automations" },
  { key: "settings", label: "Settings" },
] as const satisfies readonly AffiliateTab[];
export type BrandTab = (typeof BRAND_TABS)[number]["key"];

/** The New affiliate campaign's steps, `?step=`, in order; Setup first. */
export const NEW_CAMPAIGN_STEPS = [
  { key: "setup", label: "Setup" },
  { key: "creators", label: "Creators" },
  { key: "messages", label: "Messages" },
  { key: "automations", label: "Automations" },
  { key: "launch", label: "Create in Reacher" },
] as const satisfies readonly AffiliateTab[];
export type NewCampaignStep = (typeof NEW_CAMPAIGN_STEPS)[number]["key"];

/** The metrics page, `?t=`; Breakdown first. */
export const METRICS_TABS = [
  { key: "breakdown", label: "Breakdown" },
  { key: "funnel", label: "Funnel" },
  { key: "overtime", label: "Over time" },
  // The affiliate targets, set once for every brand.
  { key: "settings", label: "Settings" },
] as const satisfies readonly AffiliateTab[];
export type MetricsTab = (typeof METRICS_TABS)[number]["key"];

/** The Reacher writes page, `?t=`; Now first. Queue is the dispatcher's lanes. */
export const WRITES_TABS = [
  { key: "now", label: "Now" },
  { key: "queue", label: "Queue" },
  { key: "limits", label: "Limits observed" },
  { key: "errored", label: "Errored" },
  { key: "batches", label: "Batches" },
  { key: "log", label: "Every write" },
] as const satisfies readonly AffiliateTab[];
export type WritesTab = (typeof WRITES_TABS)[number]["key"];

// The DM Replies skill page's `?view=` is DM_REPLIES_VIEWS in lib/skill-calls.ts:
// the other skill pages' two, and its Metrics.

/**
 * The tab a URL value names, or the first tab — never a throw and never a tab
 * the list does not have: a typo in a pasted link lands on the page's first tab,
 * not on a blank.
 */
export function pickTab<K extends string>(tabs: readonly AffiliateTab<K>[], value: string | string[] | undefined): K {
  const v = Array.isArray(value) ? value[0] : value;
  const hit = tabs.find((t) => t.key === v);
  return (hit ?? tabs[0]).key;
}

/**
 * An address with one tab-like param set — and ⚠️ THE FIRST TAB LEAVES IT OFF, so
 * the page's plain address and its first tab are one URL rather than two that
 * mean the same thing. The lens goes first (`lensHref`), so a click is one server
 * render rather than a push and a stamping replace.
 */
export function tabHref<K extends string>(
  path: string,
  param: string,
  tabs: readonly AffiliateTab<K>[],
  key: K,
  as: string | null,
): string {
  return lensHref(key === tabs[0].key ? path : `${path}?${param}=${encodeURIComponent(key)}`, as);
}

/**
 * A brand's affiliate page, at a tab — and, from the work table, what the tab
 * opens on (`?sort=expires`, `?stage=content-unfulfilled`, …: each tab's PR
 * reads its own). The tab comes first, then the rest in the order given.
 */
export function brandPageHref(slug: string, tab: BrandTab, as: string | null, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) return tabHref(`${AFFILIATES_PATH}/${slug}`, "tab", BRAND_TABS, tab, as);
  const pairs: [string, string][] = tab === BRAND_TABS[0].key ? [] : [["tab", tab]];
  for (const [k, v] of Object.entries(params)) pairs.push([k, v]);
  return lensHref(`${AFFILIATES_PATH}/${slug}${query(pairs)}`, as);
}

/** A brand's New affiliate campaign, at a step. */
export const newCampaignHref = (slug: string, step: NewCampaignStep, as: string | null): string =>
  tabHref(`${AFFILIATES_PATH}/${slug}/new`, "step", NEW_CAMPAIGN_STEPS, step, as);
